import { retryUnlessClientError } from '@brush/api-client';
import { ActingRoleProvider, AuthProvider, RequireAuth } from '@brush/auth';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { DEV_ENTERED_KEY, DevApiProvider, MsalApiProvider } from './api';
import { App } from './App';
import { apiScope, authDisabled, msalConfig } from './authConfig';
import { LandingScreen } from './components/LandingScreen';
import { reportRequestFailure } from './membership';
import { PlayerProvider } from './player';
import './index.css';

const queryClient = new QueryClient({
  // <b>Every failed request in the app passes through these two, and that is the point.</b> A room
  // revoking a card makes the *next* request fail, whatever it happened to be — the roster, a
  // leaderboard, a contest, a fantasy team upsert — so there is no one screen that could notice it
  // and no one query worth singling out. CR4 asks for the first membership-related 403 to be the
  // signal, and this is the only place that sees all of them.
  //
  // `membership.ts` decides which failures count and what to do about it; these two handlers know
  // none of that, which is what keeps the rule in one place rather than duplicated per cache. A
  // failure that is not a membership denial — and almost all of them are not — is passed over
  // without touching anything, so the screens go on reporting their own errors as they did.
  queryCache: new QueryCache({ onError: reportRequestFailure }),
  mutationCache: new MutationCache({ onError: reportRequestFailure }),
  // A 4xx is an answer and is never asked again; anything else once (#128, `retryUnlessClientError`). A hook
  // stating its own `retry` still wins.
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: retryUnlessClientError } },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found.');

// Outside PlayerProvider: the role decides whether there is anybody to resolve a portal for at
// all — staff pick a player, a player resolves to their own record — so it has to be settled
// before that question is asked.
const app: ReactNode = (
  <ActingRoleProvider>
    <PlayerProvider>
      <App />
    </PlayerProvider>
  </ActingRoleProvider>
);

/**
 * The dev bypass has no sign-in gate at all, so the landing page has no moment to occupy and
 * would otherwise be unreachable on the one setup that needs no tenant — which is the setup it
 * will be worked on from. This puts it in front, once per tab.
 *
 * Session storage rather than local: the door is not a preference, and a developer who has just
 * changed it wants to see it again on the next reload of a fresh tab. It is also why this is a
 * dev affordance and not a feature — with MSAL on, `RequireAuth` decides, and being signed in is
 * what dismisses the page.
 */

function DevLandingGate({ children }: { children: ReactNode }) {
  const [entered, setEntered] = useState(
    () => sessionStorage.getItem(DEV_ENTERED_KEY) === 'true',
  );

  if (entered) return <>{children}</>;

  return (
    <LandingScreen
      onSignIn={() => {
        sessionStorage.setItem(DEV_ENTERED_KEY, 'true');
        setEntered(true);
      }}
    />
  );
}

// Players use the Entra External ID plane in production; dev skips MSAL entirely.
const tree: ReactNode = authDisabled ? (
  <QueryClientProvider client={queryClient}>
    <DevApiProvider>
      <DevLandingGate>{app}</DevLandingGate>
    </DevApiProvider>
  </QueryClientProvider>
) : (
  <AuthProvider config={msalConfig}>
    {/*
      The landing page renders here — inside `AuthProvider`, because its call to action is
      `loginRedirect`, and outside everything below, because none of it exists before a token.
      `RequireAuth` still owns the decision and the transient redirect-handling state; what the
      fallback replaces is only the built-in sign-in card.
    */}
    <RequireAuth
      appName="Brush Player Portal"
      variant="dark"
      scopes={apiScope ? [apiScope] : []}
      fallback={(signIn) => <LandingScreen onSignIn={signIn} />}
    >
      <QueryClientProvider client={queryClient}>
        <MsalApiProvider>{app}</MsalApiProvider>
      </QueryClientProvider>
    </RequireAuth>
  </AuthProvider>
);

createRoot(container).render(<StrictMode>{tree}</StrictMode>);
